import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { AuditTrailDTO } from '../interface/audit-trail.interface';
import {
  ConfirmModalDTO,
  GenericEditModalDTO,
  GenericTableEditFieldsDTO,
  ModalPosition,
} from '../interface/app-common.interface';
import { AllIssueDTO } from '../interface/all-issues.interface';

@Injectable({
  providedIn: 'root',
})
export class AppModalService {
  private confirmModal = new Subject<ConfirmModalDTO>();
  confirmModalState$ = this.confirmModal.asObservable();

  private taxAccountingEdit = new Subject<any>();
  taxAccountingEditSave$ = this.taxAccountingEdit.asObservable();

  private auditSubject = new Subject<AuditTrailDTO>();
  auditState$ = this.auditSubject.asObservable();

  private maintenanceEdit = new BehaviorSubject<GenericEditModalDTO | null>(null);
  maintenanceEditState$ = this.maintenanceEdit.asObservable();

  private newIssueEdit = new Subject<any>();
  newIssueState$ = this.newIssueEdit.asObservable();

  existingIssueEdit = new BehaviorSubject<{
    modalName: string;
    rowData: any;
    issueID: string;
  } | null>(null);
  existingIssueEditState$ = this.existingIssueEdit.asObservable();

  existingIssueAllData = new BehaviorSubject<any>(null);
  existingIssueAllDataState$ = this.existingIssueAllData.asObservable();

  private isEditIssueModalVisible = new BehaviorSubject<boolean>(false);
  isEditIssueVisible$ = this.isEditIssueModalVisible.asObservable();

  private detailedIssueModal = new Subject<any>();
  detailedIssueModalState$ = this.detailedIssueModal.asObservable();

  private dragging = false;
  private offsetX = 0;
  private offsetY = 0;
  private draggedElement: HTMLElement | null = null;

  /** MODAL: Confirmation Modal */
  openConfirm(source: string, title: string, message: string): void {
    this.confirmModal.next({
      source,
      title,
      message,
    });
  }

  /** MODAL: NEW_ISSUE Modal */
  openNewIssueModal(title: string, data: { isDetailView: boolean; data: AllIssueDTO }): void {
    this.newIssueEdit.next({
      title,
      data,
    });
  }

  /** MODAL: EXISTING_ISSUE Modal */
  openExistingIssueEditModal(modalName: string, rowData: any, issueID: string): void {
    this.existingIssueEdit.next({
      modalName,
      rowData,
      issueID,
    });
  }

  openExistingIssueAllData(rowData: any): void {
    this.existingIssueAllData.next(rowData);
  }

  openIssueDetailedEditModal(
    title: string,
    rowData: any = {
      issueID: '',
      idIssueCategory: 0,
      description: '',
    },
  ): void {
    this.detailedIssueModal.next({
      title,
      rowData,
    });
  }

  /** MODAL: CLOSE_EXISTING_ISSUE Modal */
  updateEditIssueStatus(status: boolean): void {
    this.isEditIssueModalVisible.next(status);
  }

  /** MODAL: TAX ACCOUNTING EDIT */
  TaxAccEditSaveTrigger(issue: any): void {
    this.taxAccountingEdit.next({
      issue,
    });
  }

  /** MODAL: AUDIT TRAIL */
  openAudit(title: string, columns: string[], data: unknown[]): void {
    this.auditSubject.next({
      title,
      columns,
      data,
    } as AuditTrailDTO);
  }

  /** MODAL: AUDIT TRAIL */
  openGenericTableEditUpadteModal(
    modalName: string,
    rowData: unknown,
    fields: GenericTableEditFieldsDTO[],
    isEdit?: boolean,
  ): void {
    this.maintenanceEdit.next({
      title: `Edit, ${modalName}, ${(rowData as any).id}`,
      modalName,
      rowData,
      fields,
      isEdit,
    });
  }

  closeMaintenanceEdit(): void {
    this.maintenanceEdit.next(null);
  }

  /** MODAL_ALIGNMENT - Responsive centering with fixed top offset */
  centerModal(
    modalElement: HTMLElement,
    topDesktop = 100, // 100px
    topMobile = 40, // 40px
  ): ModalPosition {
    const modalWidth = modalElement.offsetWidth;
    const availableWidth = window.innerWidth;

    return {
      positionX: Math.max(0, Math.round((availableWidth - modalWidth) / 2)),
      positionY: window.innerWidth <= 1440 ? topMobile : topDesktop,
    };
  }

  /** MODAL_DRAG - Track which element is being dragged */
  startDrag(event: MouseEvent, currentX: number, currentY: number, element?: HTMLElement): void {
    this.dragging = true;
    if (element) {
      this.draggedElement = element;
    }
    this.offsetX = event.clientX - currentX;
    this.offsetY = event.clientY - currentY;
    document.body.style.userSelect = 'none';
  }

  /** MODAL_DRAG - Only update if this element is being dragged */
  drag(event: MouseEvent, element?: HTMLElement): ModalPosition | null {
    if (!this.dragging) {
      return null;
    }
    if (element && this.draggedElement && this.draggedElement !== element) {
      return null;
    }
    return {
      positionX: event.clientX - this.offsetX,
      positionY: event.clientY - this.offsetY,
    };
  }

  /** MODAL_DRAG - Stop dragging and clear reference */
  stopDrag(): void {
    this.dragging = false;
    this.draggedElement = null;
    document.body.style.userSelect = '';
  }
}
